import { getPersonAvatarInitials } from '@condo/domain';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { cn } from '@/shared/utils';

export type RamAvatarSize = 'default' | 'compact';

export interface RamAvatarProps {
    imageSrc?: string | null;
    /** Display string for `alt` and initials; parent supplies the label to show. */
    fullname?: string | null;
    /** `compact` — smaller circle for dense rows (e.g. selects). */
    size?: RamAvatarSize;
    /** Merged onto the root `Avatar` (e.g. `h-full w-full` for stretch layouts). */
    className?: string;
}

const SIZE_STYLES: Record<RamAvatarSize, { root: string; fallbackText: string }> = {
    default: { root: 'h-7 w-7', fallbackText: 'text-[10px]' },
    compact: { root: 'h-5 w-5', fallbackText: 'text-[9px]' },
};

/**
 * Person avatar: image when `imageSrc` is set, otherwise soft primary-tinted initials (RAM standard for users).
 * Remounts when `imageSrc` or `fullname` changes so Radix avatar state stays in sync (e.g. selects).
 */
export function RamAvatar({ imageSrc, fullname, size = 'default', className }: RamAvatarProps) {
    const initials = getPersonAvatarInitials(fullname);
    const alt = fullname?.trim() || 'User';
    const avatarInstanceKey = `${imageSrc ?? ''}|${fullname ?? ''}`;
    const dim = SIZE_STYLES[size];

    return (
        <Avatar key={avatarInstanceKey} className={cn(dim.root, 'shrink-0', className)}>
            {imageSrc ? <AvatarImage key={imageSrc} src={imageSrc} alt={alt} /> : null}
            <AvatarFallback className={cn('bg-primary/15 font-medium text-primary', dim.fallbackText)}>{initials}</AvatarFallback>
        </Avatar>
    );
}
